/**
 * @format
 * @module ui/register
 */
import extend from "lodash/extend";
import consts from "lib/consts";
import ee from "lib/eventemitter";
import { configureModal } from "ui/modal-manager";
import { isDesktop } from "utils/detect";
import logger from "utils/logger";
import { isFunctionThenInvoke } from "utils/utils";
import { setAfterLoginCookie, isLoggedIn } from "lib/identity/api";
import { setProp, cacheToken } from "utils/utils";

const debugTag = "[REGISTER]";

const defaultConfig = {
  uid: "register-modal",
  topPaddingForCloseButton: false,
  sourceElement: ".js-onclick--register",
  scrollable: true,
  iframeUrl: consts.URLS.MODALS.REGISTER,
  height: "700"
};

/**
 * @method configureModal
 * @description will create an instance of the register modal with default configurations
 */
export let showRegisterModal = () => {};

/**
 * @method init
 * @description bootstrap register modal
 */
export function init() {
  if (isLoggedIn()) {
    return;
  }
  showRegisterModal = configureModal(defaultConfig);
  setProp(window, "tgam.ui.register.show", uiRegisterShow);
}

/**
 * @method uiRegisterShow
 * @description Allow optional url override for modal iframe
 * @param {Object} [c={}] - collection of params
 * @param {String} [c.url] - for iframe src
 */
function uiRegisterShow({ url } = {}) {
  // We need a random token, in case Darwin calls this method multiple times.
  let customConfig = { uid: `${defaultConfig.uid}-darwin-${cacheToken()}` };
  if (url) {
    customConfig.iframeUrl = url;
  }
  const darwinConfig = extend({}, defaultConfig, customConfig);
  const showRegisterModal = configureModal(darwinConfig);
  willShowRegisterModal(showRegisterModal, darwinConfig.iframeUrl);
}

/**
 * @method willShowRegisterModal
 * @description will either show a register modal or redirect you to the register page
 * @param {Function} callback - method to invoke to display modal
 * @param {String} [mobileUrl=consts.URLS.REGISTER.BASE] - for mobile redirection
 */
export function willShowRegisterModal(callback, mobileUrl = consts.URLS.REGISTER.BASE) {
  if (isDesktop()) {
    logger.info(`${debugTag} DESKTOP detected, show modal`);
    isFunctionThenInvoke(callback);
  } else {
    logger.info(`${debugTag} NOT DESKTOP detect, redirect`);
    window.location.href = mobileUrl;
  }
}

/**
 * @method bootstrapProtectedRegisterModal
 * @description creates a version of the register modal which will override the close function and not allow it to close
 * @param {Function} callback - for on close event
 * @param {String} [campaign] - value to attach to modal's iframe URL for tracking purposes
 * @returns {Function}
 */
export function bootstrapProtectedRegisterModal(callback, campaign) {
  // extends default object with a different uid and onCloseFunction
  let protectedModalConfig = extend({}, defaultConfig, {
    uid: "register-protected",
    onCloseFunction: callback
  });

  if (campaign) {
    protectedModalConfig.iframeUrl = defaultConfig.iframeUrl + `?intcmp=${campaign}`;
  }

  return configureModal(protectedModalConfig);
}

/**
 * @method registerAnchorClick
 * @description event listener function for when the register button is clicked
 * @param {MouseEvent} e - a mouse click event
 * @param {String} featureLabel - feature name to report
 */
export function registerAnchorClick(e, featureLabel = "") {
  logger.info(`${debugTag} registerAnchorClick()`, e);

  // if the optional attribute, then set after login cookie url for mobile only
  const after = e.target.dataset.afterLoginGoto;

  if (!isDesktop() && after) {
    setAfterLoginCookie(after);
  }
  // only show modal when in desktop view
  // if in tablet or mobile view, the anchor tag will follow the href value
  if (isDesktop()) {
    e.preventDefault();

    const a = e.target.closest("a");
    const url = a ? a.getAttribute("href").replace("user/", "user/modal/") : null;
    logger.info(`${debugTag} registerAnchorClick()`, e, a, url);
    showRegisterModal(url);
  }

  // Don't trigger a second event if the clicked element already pushes data via data-analyticsclick
  if (!e.target.closest("[data-analyticsclick]")) {
    ee.trigger("analytics.trackGTMEvent", [
      {
        feature: featureLabel || "unknown",
        type: "link",
        label: "register"
      }
    ]);
  }
  // propagation must be stopped here otherwise the document.click listener in data-collection
  // will be triggered resulting in:
  // 1. the analyticsLayer receiving two clickEvents
  // 2. the modal will not be shown -- the anchor href will be followed (because of #1)
  e.stopPropagation();
}
